import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

export default function LinkedChatsBar({ current_booking, linkedChats, messagesPagination, showMemoryWarning, setShowMemoryWarning }) {

    // Helper function to get robot color based on booking status
    const getRobotColor = (booking, user) => {
        // Check for bot flow active (yellow)
        if (booking?.bot_flow_id && booking.bot_flow_id > 0) {
            return "bg-yellow-400";
        }
        
        // Check if last message was from user (Red - needs attention) or from staff (Green - waiting for user)
        // Red if: last_message_from matches user's cell, id, email, booking id, or is -2
        const lastFrom = booking?.last_message_from;
        
        // If last_message_from is null, undefined, or empty, treat as red (needs attention)
        if (lastFrom === null || lastFrom === undefined || lastFrom === '') {
            return "bg-red-400";
        }
        
        const lastFromStr = String(lastFrom);
        const userId = String(user?.id || '');
        const userCell = String(user?.cell || '');
        const userEmail = String(user?.email || '');
        const bookingId = String(booking?.id || '');
        
        // Check if last message was from user (Red)
        const isFromUser = (
            (userCell && lastFromStr === userCell) ||
            (userId && lastFromStr === userId) ||
            (bookingId && lastFromStr === bookingId) ||
            (userEmail && lastFromStr === userEmail) ||
            lastFromStr === '-2'
        );
        
        if (isFromUser) {
            return "bg-red-400";
        }
        
        // Check if last message was from staff (Green) - specifically -1 or staff ID
        if (lastFromStr === '-1' || (!isFromUser && lastFromStr !== '')) {
            return "bg-green-400";
        }
        
        // Default fallback is red (needs attention)
        return "bg-red-400";
    };

    // Helper function to truncate name to 30 characters
    const truncateName = (name) => {
        if (!name) return "";
        return name.length > 30 ? name.substring(0, 30) + "..." : name;
    };

    // Get linked booking data from linked chats
    const getLinkedBookings = () => {
        if (!linkedChats || linkedChats.length === 0) {
            return [];
        }
        
        // Collect all unique bookings (excluding current booking)
        const bookingsMap = new Map();
        
        linkedChats.forEach((chat) => {
            // Process both sides of the link
            const bookings = [
                { data: chat?.booking, user: chat?.user, user_id: chat?.user_id, booking_id: chat?.booking_id },
                { data: chat?.linked_booking, user: chat?.linked_user, user_id: chat?.linked_user_id, booking_id: chat?.linked_booking_id }
            ];
            
            bookings.forEach(({ data, user, user_id, booking_id }) => {
                // Skip if this is the current booking or if data is missing
                if (!data || data.id === current_booking?.id) return;
                
                // Skip if we've already added this booking
                if (bookingsMap.has(data.id)) return;
                
                bookingsMap.set(data.id, {
                    id: data.id,
                    booking_reference: data.booking_reference,
                    name: `${user?.name || ""} ${user?.surname || ""}`.trim(),
                    location: data.location,
                    user_id: user?.id,
                    tenant_type: data.tenant_type,
                    mda_balance: data.user_mda?.balance || null,
                    robotColor: getRobotColor(data, user),
                    url: `/booking/${data.location?.toLowerCase().replace(/\s+/g, "")}?user_id=${user?.id}&booking_id=${data.id}`
                });
            });
        });
        
        return Array.from(bookingsMap.values());
    };

    const linkedBookings = getLinkedBookings();
    const currentBookingName = `${current_booking?.user?.name || ""} ${current_booking?.user?.surname || ""}`.trim();
    const currentMdaBalance = current_booking?.user_mda?.balance;

    // Copy to clipboard helper
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                alert("Copied to clipboard!");
            })
            .catch((err) => {
                console.error("Failed to copy: ", err);
            });
    };

    // Sort linked bookings by robot color priority (red first, yellow second, green last)
    const sortedLinkedBookings = [...linkedBookings].sort((a, b) => {
        const getPriority = (booking) => {
            const color = booking.robotColor;
            if (color === "bg-red-400") return 1; // Highest priority
            if (color === "bg-yellow-400") return 2;
            if (color === "bg-green-400") return 3; // Lowest priority
            return 4;
        };
        return getPriority(a) - getPriority(b);
    });

    return (
        <>
            {/* Linked Chats Bar - Orange */}
            <div className="px-3 py-2 border-b border-orange-300 bg-orange-50">
                <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-orange-300 scrollbar-track-orange-100 pb-1">
                    {/* Current Booking - Active (Always First) */}
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border-2 border-green-400 rounded flex-shrink-0">
                        <div className={classNames(
                            getRobotColor(current_booking, current_booking?.user),
                            "flex items-center justify-center h-6 w-6 rounded-full ring-1 ring-white flex-shrink-0"
                        )}>
                            <span className="text-white text-xs font-bold">
                                {current_booking?.tenant_type || ""}
                            </span>
                        </div>
                        <div className="flex flex-col">
                            <div className="flex items-center gap-1.5">
                                <span
                                    className="text-xs font-semibold text-gray-900 underline cursor-pointer hover:text-gray-700"
                                    onClick={() => copyToClipboard(current_booking?.booking_reference)}
                                    title="Click to copy booking reference"
                                >
                                    {current_booking?.booking_reference}
                                </span>
                            </div>
                            <span className="text-xs text-gray-700">
                                {truncateName(currentBookingName)}
                            </span>
                            <span className="text-xs text-gray-600">
                                {current_booking?.location}
                            </span>
                            {current_booking?.tenant_type === 'LH' && currentMdaBalance !== null && currentMdaBalance !== undefined && (
                                <span className={`text-xs font-semibold ${
                                    currentMdaBalance >= 0 ? 'text-green-600' : 'text-red-600'
                                }`}>
                                    R {currentMdaBalance?.toLocaleString()}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Linked Chats - Sorted by robot color (red, yellow, green) */}
                    {sortedLinkedBookings.map((booking) => (
                        <div
                            key={booking.id}
                            onClick={() => window.location.href = booking.url}
                            className="flex items-center gap-1.5 px-2 py-1 bg-white border border-orange-200 rounded hover:bg-orange-50 hover:border-orange-300 transition-colors group flex-shrink-0 cursor-pointer"
                        >
                            <div className={classNames(
                                booking.robotColor,
                                "flex items-center justify-center h-6 w-6 rounded-full ring-1 ring-white flex-shrink-0"
                            )}>
                                <span className="text-white text-xs font-bold">
                                    {booking.tenant_type || ""}
                                </span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs text-orange-600 group-hover:text-orange-700">
                                    {truncateName(booking.name)}
                                </span>
                                <span className="text-xs text-orange-500">
                                    {booking.location}
                                </span>
                                {booking.tenant_type === 'LH' && booking.mda_balance !== null && booking.mda_balance !== undefined && (
                                    <span className={`text-xs font-semibold ${
                                        booking.mda_balance >= 0 ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                        R {booking.mda_balance?.toLocaleString()}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Memory Warning Bar - Collapsible - Below Linked Chats */}
            {messagesPagination?.has_more && showMemoryWarning && (
                <div className="px-2 py-1.5 border-b border-red-300 bg-red-50 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={() => setShowMemoryWarning(false)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            title="Hide warning"
                        >
                            <FontAwesomeIcon icon={faTriangleExclamation} className="w-3.5 h-3.5" />
                        </button>
                        <span className="text-xs font-semibold text-red-800">Large Chat</span>
                        <button
                            onClick={() => {
                                if (messagesPagination?.onLoadMore && !messagesPagination?.loading) {
                                    messagesPagination.onLoadMore();
                                }
                            }}
                            disabled={messagesPagination?.loading}
                            className={`ml-2 px-2 py-0.5 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors ${
                                messagesPagination?.loading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            title={messagesPagination?.loading ? "Loading..." : "Load more messages"}
                        >
                            {messagesPagination?.loading ? 'Loading...' : 'Load More'}
                        </button>
                    </div>
                    <span className="text-xs text-red-700">
                        {messagesPagination.loaded?.toLocaleString() || 0} / {messagesPagination.total?.toLocaleString() || 0}
                    </span>
                </div>
            )}
        </>
    );
}
